import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '../../configs/config';

export default function ReviewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const reviewId = searchParams.get('review_id');
  const asin = searchParams.get('asin');

  const [productInfo, setProductInfo] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [currentStage, setCurrentStage] = useState('preprocess');
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [stages, setStages] = useState({ preprocess: false, query: false, review: false });
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(true);
  const [latestEvent, setLatestEvent] = useState(null);

  const eventSourceRef = useRef(null);
  const eventsEndRef = useRef(null);
  const eventsContainerRef = useRef(null);
  const processedEventsRef = useRef(new Set());
  const hasSetProductDetailsRef = useRef(false);

  const getOfficialPatentId = (patentId) => {
    return patentId.split('-')[0];
  };

  useEffect(() => {
    if (!reviewId || !asin) {
      navigate('/analyze');
      return;
    }

    const connectToStream = () => {
      try {
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
          eventSourceRef.current = null;
        }

        processedEventsRef.current.clear();
        setEvents([]);

        const eventSource = new EventSource(
          `${APP_CONFIG.API_BASE_URL}/patent-review/stream-progress/${reviewId}`
        );
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
          try {
            const progressData = JSON.parse(event.data);
            
            const eventData = progressData.data || progressData;

            setCurrentStage(eventData.current_stage);
            setProgressPercentage(eventData.progress_percentage);
            setStages(eventData.stages);

            if (eventData.stage_details) {
              if (eventData.stage_details.product && !hasSetProductDetailsRef.current) {
                setProductDetails(eventData.stage_details.product);
                hasSetProductDetailsRef.current = true;
              }

              if (eventData.current_stage === 'preprocess' && eventData.stage_details.areas_identified) {
                setProductInfo(prev => ({
                  ...prev,
                  areas: eventData.stage_details.areas_identified
                }));
              }

              const eventNumber = progressData.event_number;
              const eventTimestamp = progressData.timestamp || new Date().toISOString();
              const eventKey = `${eventNumber}-${eventTimestamp}`;
              
              if (!processedEventsRef.current.has(eventKey)) {
                processedEventsRef.current.add(eventKey);
                const newEvent = {
                  eventNumber: eventNumber,
                  timestamp: eventTimestamp,
                  stage: eventData.current_stage,
                  message: eventData.stage_details.message,
                  details: eventData.stage_details,
                  progress: eventData.progress_percentage
                };
                setEvents(prev => [...prev, newEvent]);
                setLatestEvent(newEvent);
              }
            }

            if (eventData.status === 'completed') {
              setIsAnalyzing(false);
              eventSource.close();
              
              setTimeout(async () => {
                const { searchHistoryUtil } = await import('../../utils/searchHistory');
                const historyEntry = {
                  query: `Patent Review Analysis: ${asin}`,
                  asin: asin,
                  resultId: reviewId,
                };
                
                if (productDetails?.title) {
                  historyEntry.productTitle = productDetails.title;
                }
                if (productDetails?.images && productDetails.images.length > 0) {
                  historyEntry.productImageUrl = productDetails.images[0];
                }
                
                searchHistoryUtil.addSearch(historyEntry);
                
                navigate(`/analyze/results?result_id=${reviewId}&asin=${asin}`);
              }, 2000);
            } else if (eventData.status === 'failed') {
              setError(eventData.error_message || 'Analysis failed');
              setIsAnalyzing(false);
              eventSource.close();
            }
          } catch (err) {
            console.error('Error parsing progress:', err);
          }
        };

        eventSource.onerror = (err) => {
          console.error('EventSource error:', err);
          eventSource.close();
          setError('Connection to progress stream lost');
          setIsAnalyzing(false);
        };
      } catch (err) {
        setError(err.message || 'Failed to connect to progress stream');
        setIsAnalyzing(false);
      }
    };

    connectToStream();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [reviewId, asin, navigate, productDetails]);

  useEffect(() => {
    if (eventsEndRef.current) {
      eventsEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  }, [events]);

  if (!asin) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-8">
        <div className="max-w-md w-full bg-red-500/90 text-white rounded-2xl p-8 space-y-4 text-center">
          <svg className="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-lg font-semibold">{error}</p>
          <button
            onClick={() => navigate('/analyze')}
            className="px-6 py-2 bg-white text-red-600 rounded-xl font-semibold hover:bg-gray-100 transition-all"
          >
            Back to Analyze
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col px-4 py-8">
      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% {
            transform: translateY(0);
            animation-timing-function: cubic-bezier(0.8, 0, 1, 1);
          }
          50% {
            transform: translateY(-10px);
            animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
          }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s infinite;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .event-fade-in {
          animation: fadeInUp 0.5s ease-out;
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .insight-fade-in {
          animation: fadeIn 1s ease-out;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.5);
        }
      `}</style>
      <div className="max-w-7xl mx-auto w-full flex flex-col h-full">
        <div className="flex items-center justify-between mb-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/analyze')}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-semibold transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back
            </button>
            
            {/* Jumping Company Icon */}
            <div className="relative">
              <div className="animate-bounce-slow">
                <img 
                  src="/tadreamk_logo.svg" 
                  alt="TadReamk" 
                  className="w-8 h-8"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-white">
            Patent Review Analysis
          </h1>
          
          <div className="w-20"></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
          <div className="w-full lg:w-1/3 flex flex-col gap-4 min-h-0">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 overflow-y-auto custom-scrollbar" style={{ maxHeight: '60%' }}>
              <h2 className="text-xl font-bold text-white mb-4">Product Information</h2>
              
              <div className="space-y-4">
                <div>
                  <p className="text-gray-400 text-sm">ASIN</p>
                  <p className="text-white font-semibold">{asin}</p>
                </div>

                {!productDetails && (
                  <div className="space-y-3">
                    <div className="w-full h-48 bg-white/5 rounded-lg animate-pulse"></div>
                    <div className="h-4 bg-white/5 rounded animate-pulse"></div>
                    <div className="h-4 bg-white/5 rounded animate-pulse w-3/4"></div>
                  </div>
                )}

                {productDetails?.images && productDetails.images.length > 0 && (
                  <div>
                    <img 
                      src={productDetails.images[0]} 
                      alt={productDetails.title || 'Product'}
                      className="w-full rounded-lg border border-white/20 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                )}

                {productDetails?.brand && (
                  <div>
                    <p className="text-gray-400 text-sm">Brand</p>
                    <p className="text-white font-semibold">{productDetails.brand}</p>
                  </div>
                )}

                {productDetails?.category && (
                  <div>
                    <p className="text-gray-400 text-sm">Category</p>
                    <p className="text-white font-semibold">{productDetails.category}</p>
                  </div>
                )}

                {productDetails?.title && (
                  <div>
                    <p className="text-gray-400 text-sm">Title</p>
                    <p className="text-white text-sm">{productDetails.title}</p>
                  </div>
                )}

                {productDetails?.description && (
                  <div>
                    <p className="text-gray-400 text-sm">Description</p>
                    <p className="text-white text-sm">{productDetails.description}</p>
                  </div>
                )}

                {productDetails?.feature_bullets && productDetails.feature_bullets.length > 0 && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Key Features</p>
                    <div className="space-y-2">
                      {productDetails.feature_bullets.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="text-purple-400 mt-1 flex-shrink-0">•</span>
                          <p className="text-white text-xs flex-1">{feature}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {productInfo?.areas && (
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Identified Areas</p>
                    <div className="space-y-2">
                      {productInfo.areas.map((area, index) => (
                        <div key={index} className="bg-white/5 rounded-lg p-2 border border-white/10">
                          <p className="text-white text-sm">{area}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex-shrink-0">
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white text-sm font-semibold">Overall Progress</span>
                  <span className="text-white text-sm font-bold">{progressPercentage}%</span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div 
                    className="bg-gradient-to-r from-blue-500 to-purple-500 h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-gray-400 text-sm mb-3">Stage Progress</p>
                {['preprocess', 'query', 'review'].map((stage) => (
                  <div key={stage} className="flex items-center gap-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      stages[stage] 
                        ? 'bg-green-500' 
                        : currentStage === stage 
                        ? 'bg-yellow-500 animate-pulse' 
                        : 'bg-white/10'
                    }`}>
                      {stages[stage] && (
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <span className={`text-sm font-semibold capitalize ${
                      stages[stage] || currentStage === stage ? 'text-white' : 'text-gray-400'
                    }`}>
                      {stage}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-4 min-h-0">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex flex-col" style={{ height: '40%' }}>
              <h2 className="text-xl font-bold text-white mb-4 flex-shrink-0">Analysis Progress</h2>
              
              <div 
                ref={eventsContainerRef}
                className="space-y-4 overflow-y-auto flex-1 pr-2 custom-scrollbar"
                style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.3) transparent' }}
              >
            {events.map((event, index) => (
              <div 
                key={`${event.eventNumber}-${event.timestamp}`} 
                className="bg-white/5 rounded-xl p-4 border border-white/10 event-fade-in"
              >
                <div className="flex items-start gap-3 mb-2">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    event.stage === 'preprocess' 
                      ? 'bg-blue-500' 
                      : event.stage === 'query' 
                      ? 'bg-purple-500' 
                      : 'bg-green-500'
                  }`}>
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <span className="text-white font-semibold capitalize">{event.stage}</span>
                      <span className="text-purple-300 text-xs font-semibold">{event.progress}%</span>
                    </div>
                    <p className="text-white text-sm mb-2">{event.message}</p>
                    
                    {event.details.areas_identified && (
                      <div className="mt-2 space-y-1">
                        {event.details.areas_identified.map((area, idx) => (
                          <div key={idx} className="text-gray-300 text-xs pl-3 border-l-2 border-blue-400">
                            {area}
                          </div>
                        ))}
                      </div>
                    )}

                    {event.details.search_area && (
                      <div className="mt-2 text-gray-300 text-xs">
                        <span className="font-semibold">Area:</span> {event.details.search_area}
                      </div>
                    )}

                    {event.details.search_result_id && (
                      <div className="mt-2">
                        <button
                          onClick={() => navigate(`/search?id=${event.details.search_result_id}&query=${encodeURIComponent(event.details.search_area || 'Search Results')}`)}
                          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold transition-all border border-white/20 flex items-center gap-1.5"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          View Search Results
                        </button>
                      </div>
                    )}

                    {event.details.current_patent_id && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-gray-300 text-xs">
                            <span className="font-semibold">Patent:</span> 
                            <span className="ml-1 font-mono">{event.details.current_patent_id}</span>
                          </div>
                          {event.details.risk_level && (
                            <span className={`text-xs font-semibold ${
                              event.details.risk_level === 'HIGH' 
                                ? 'text-red-400' 
                                : event.details.risk_level === 'MEDIUM' 
                                ? 'text-yellow-400' 
                                : 'text-green-400'
                            }`}>
                              {event.details.risk_level}
                            </span>
                          )}
                        </div>
                        {event.details.patent_title && (
                          <div className="text-gray-300 text-xs">
                            <span className="font-semibold">Title:</span> {event.details.patent_title}
                          </div>
                        )}
                        <button
                          onClick={() => navigate(`/patent?id=${encodeURIComponent(getOfficialPatentId(event.details.current_patent_id))}&return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                          className="w-full px-3 py-1.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
                        >
                          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          View Details
                        </button>
                      </div>
                    )}

                    {event.details.search_result_ids && (
                      <div className="mt-2 text-gray-300 text-xs">
                        <span className="font-semibold">Search Results:</span> {Object.keys(event.details.search_result_ids).length} areas completed
                      </div>
                    )}

                    {event.details.total_reviewed !== undefined && (
                      <div className="mt-2 text-gray-300 text-xs">
                        <span className="font-semibold">Total Reviewed:</span> {event.details.total_reviewed} patents
                      </div>
                    )}

                    <p className="text-gray-400 text-xs mt-2">
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {isAnalyzing && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/10 flex items-center gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
                  <svg className="w-5 h-5 text-white animate-spin" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
              </div>
            )}

            <div ref={eventsEndRef} />
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 flex flex-col" style={{ height: '60%' }}>
              <h2 className="text-xl font-bold text-white mb-4 flex-shrink-0">Event Insights</h2>
              
              <div 
                key={latestEvent ? `${latestEvent.eventNumber}-${latestEvent.timestamp}` : 'empty'}
                className="overflow-y-auto flex-1 pr-2 custom-scrollbar insight-fade-in"
              >
                {!latestEvent ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-gray-400 text-sm text-center">No event details available yet. Analysis in progress...</p>
                  </div>
                ) : latestEvent.stage === 'review' && latestEvent.details.current_patent_id ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-gray-400 text-xs mb-2">Patent ID</p>
                        <p className="text-white text-sm font-mono">{latestEvent.details.current_patent_id}</p>
                      </div>
                      {latestEvent.details.risk_level && (
                        <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                          latestEvent.details.risk_level === 'HIGH' 
                            ? 'bg-red-500/20 text-red-400' 
                            : latestEvent.details.risk_level === 'MEDIUM' 
                            ? 'bg-yellow-500/20 text-yellow-400' 
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {latestEvent.details.risk_level}
                        </div>
                      )}
                    </div>

                    {latestEvent.details.patent_title && (
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Title</p>
                        <p className="text-white text-sm">{latestEvent.details.patent_title}</p>
                      </div>
                    )}

                    <button
                      onClick={() => navigate(`/patent?id=${encodeURIComponent(getOfficialPatentId(latestEvent.details.current_patent_id))}&return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
                      className="w-full py-2.5 px-4 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-lg font-semibold transition-all hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      View Patent Details
                    </button>

                    {latestEvent.details.risk_summary && (
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Risk Summary</p>
                        <p className="text-white text-sm leading-relaxed">{latestEvent.details.risk_summary}</p>
                      </div>
                    )}

                    {latestEvent.details.risky_claims && latestEvent.details.risky_claims.length > 0 && (
                      <div>
                        <p className="text-gray-400 text-xs mb-2">Claims Analysis</p>
                        <div className="space-y-3">
                          {latestEvent.details.risky_claims.map((claim, idx) => (
                            <div key={idx} className="bg-white/5 rounded-lg p-3 border border-white/10">
                              <p className="text-purple-400 text-xs font-semibold mb-2">Claim {claim.claim_number}</p>
                              <p className="text-white text-xs mb-2 line-clamp-3">{claim.claim_text}</p>
                              {claim.matching_product_features && claim.matching_product_features.length > 0 && (
                                <div className="mt-2">
                                  <p className="text-gray-400 text-xs mb-1">Matching Features:</p>
                                  <ul className="space-y-1">
                                    {claim.matching_product_features.map((feature, fIdx) => (
                                      <li key={fIdx} className="text-gray-300 text-xs pl-3 flex items-start gap-2">
                                        <span className="text-purple-400 flex-shrink-0">•</span>
                                        <span className="flex-1">{feature}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : latestEvent.stage === 'query' ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Stage</p>
                      <p className="text-white text-sm font-semibold capitalize">{latestEvent.stage}</p>
                    </div>

                    {latestEvent.details.search_area && (
                      <div>
                        <p className="text-gray-400 text-xs mb-1">Search Area</p>
                        <p className="text-white text-sm">{latestEvent.details.search_area}</p>
                      </div>
                    )}

                    {latestEvent.details.search_result_id && (
                      <div>
                        <p className="text-gray-400 text-xs mb-2">Search Results</p>
                        <button
                          onClick={() => navigate(`/search?id=${latestEvent.details.search_result_id}&query=${encodeURIComponent(latestEvent.details.search_area || 'Search Results')}`)}
                          className="w-full py-2 px-4 bg-white/10 hover:bg-white/20 text-white rounded-lg font-semibold transition-all border border-white/20 flex items-center justify-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                          View Search Results
                        </button>
                      </div>
                    )}

                    {latestEvent.details.current_search && latestEvent.details.total_searches && (
                      <div>
                        <p className="text-gray-400 text-xs mb-2">Progress</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-white/10 rounded-full h-2">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full transition-all duration-500"
                              style={{ width: `${(latestEvent.details.current_search / latestEvent.details.total_searches) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-white text-xs font-semibold">
                            {latestEvent.details.current_search}/{latestEvent.details.total_searches}
                          </span>
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-gray-400 text-xs mb-1">Message</p>
                      <p className="text-white text-sm">{latestEvent.message}</p>
                    </div>
                  </div>
                ) : latestEvent.stage === 'preprocess' ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Stage</p>
                      <p className="text-white text-sm font-semibold capitalize">{latestEvent.stage}</p>
                    </div>

                    <div>
                      <p className="text-gray-400 text-xs mb-1">Message</p>
                      <p className="text-white text-sm">{latestEvent.message}</p>
                    </div>

                    {latestEvent.details.areas_identified && latestEvent.details.areas_identified.length > 0 && (
                      <div>
                        <p className="text-gray-400 text-xs mb-2">Identified Areas</p>
                        <div className="space-y-2">
                          {latestEvent.details.areas_identified.map((area, idx) => (
                            <div key={idx} className="bg-white/5 rounded-lg p-3 border border-white/10">
                              <p className="text-white text-sm">{area}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {productDetails && (
                      <div>
                        <p className="text-gray-400 text-xs mb-2">Product Summary</p>
                        <div className="bg-white/5 rounded-lg p-3 border border-white/10 space-y-2">
                          {productDetails.brand && (
                            <div>
                              <span className="text-gray-400 text-xs">Brand: </span>
                              <span className="text-white text-xs">{productDetails.brand}</span>
                            </div>
                          )}
                          {productDetails.category && (
                            <div>
                              <span className="text-gray-400 text-xs">Category: </span>
                              <span className="text-white text-xs">{productDetails.category}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Stage</p>
                      <p className="text-white text-sm font-semibold capitalize">{latestEvent.stage}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-1">Message</p>
                      <p className="text-white text-sm">{latestEvent.message}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

